import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Newspaper,
  TrendingUp,
  Search
} from '@mui/icons-material';

const NotFound = () => {
  const suggestedArticles = [
    {
      title: "Latest News from Ethiopia",
      excerpt: "Stay updated with the most recent developments across Ethiopia and East Africa.",
      category: "Politics",
      readTime: "3 min read"
    },
    {
      title: "Business Updates and Economic News",
      excerpt: "Get insights into the latest business developments and economic trends.",
      category: "Business",
      readTime: "4 min read"
    },
    {
      title: "Sports Highlights and Achievements",
      excerpt: "Coverage of local and international sports events featuring Ethiopian athletes.",
      category: "Sports",
      readTime: "5 min read"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8, mt: 10 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* 404 Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '4rem', md: '6rem' },
              fontWeight: 'bold',
              color: 'primary.main',
              mb: 2
            }}
          >
            404
          </Typography>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 2,
              color: 'text.primary'
            }}
          >
            Page Not Found
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            The page you're looking for doesn't exist or has been moved. 
            Don't worry, we have plenty of great content for you to explore.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<Home />}
              sx={{ px: 4, py: 1.5 }}
            >
              Go Home
            </Button>
            <Button
              component={Link}
              to="/politics-news"
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<Newspaper />}
              sx={{ px: 4, py: 1.5 }}
            >
              Latest News
            </Button>
          </Box>
        </Box>

        {/* Suggested Content */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 4,
              textAlign: 'center',
              color: 'text.primary'
            }}
          >
            Explore Our Latest Content
          </Typography>
          
          <Grid container spacing={3}>
            {suggestedArticles.map((article, index) => (
              <Grid xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={article.category}
                          color="primary"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {article.readTime}
                        </Typography>
                      </Box>
                      
                      <Typography
                        variant="h6"
                        component="h4"
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          mb: 2,
                          lineHeight: 1.3
                        }}
                      >
                        {article.title}
                      </Typography>
                      
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 3,
                          lineHeight: 1.6
                        }}
                      >
                        {article.excerpt}
                      </Typography>
                      
                      <Button
                        component={Link}
                        to={`/${article.category.toLowerCase()}-news`}
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mt: 'auto' }}
                      >
                        Read More
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Quick Navigation */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 3,
              color: 'text.primary'
            }}
          >
            Quick Navigation
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { name: 'Politics', path: '/politics-news', icon: <Newspaper /> },
              { name: 'Business', path: '/business-news', icon: <TrendingUp /> },
              { name: 'Sports', path: '/sports-news', icon: <Search /> },
              { name: 'About Us', path: '/about', icon: <Home /> },
              { name: 'Contact', path: '/contact', icon: <Home /> }
            ].map((item) => (
              <Button
                key={item.name}
                component={Link}
                to={item.path}
                variant="outlined"
                color="primary"
                startIcon={item.icon}
                sx={{
                  textTransform: 'none',
                  px: 3,
                  py: 1
                }}
              >
                {item.name}
              </Button>
            ))}
          </Box>
        </Box>
      </motion.div>
    </Container>
  );
};

export default NotFound; 